/*
 * The router and the auth gate.
 *
 * REAL URLS, one per screen, because principle 1 makes them a contract: every
 * screen is bookmarkable, shareable and reloadable, and the back button walks the
 * history a user actually built. That rules out a single-URL app with internal
 * state, however much simpler that would be to wire.
 *
 * WHAT IS NOT IN THE URL: the current list. §2 holds it server-side per user
 * precisely because these links are never shared — a coach's list selection is
 * session state, not a location, and putting it in the path would make a
 * bookmarked link mean something different next month.
 *
 * THE GATE IS THREE STATES, NOT TWO, and this is the whole reason the API client
 * distinguishes HTTP statuses instead of throwing one Error type:
 *
 *   401 / no user   -> sign in. Nobody is here.
 *   403 / pending   -> the pending screen. Signed in, awaiting human review.
 *                      §12 requires a screen that names the mechanism, and it is
 *                      only reachable because the server answers 403 rather than
 *                      401 for an unapproved account (session.go).
 *   409 / no list   -> onboarding. Approved, but never finished setting up.
 *
 * Collapsing pending into unauthenticated would loop a waiting user back to a
 * sign-in form that accepts their correct password and then bounces them again —
 * which is precisely the "invite-only" experience §12's copy exists to prevent.
 *
 * THE LANDING RULE comes from the navigation graph: "Active if a tracked
 * tournament is live on FTL, else Upcoming." That is a data question, so "/" is a
 * decision rather than a screen — see Landing.
 *
 * EVERY SCREEN IN §§9-12 NOW EXISTS. The placeholder component that stood in for
 * the later ones is deleted rather than left in place: an "honest placeholder" is
 * only honest while something is genuinely missing, and one kept around after its
 * last route is wired is just dead code that makes the router look unfinished.
 */

import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";

import { ListScope } from "../components/ListScope";
import { railFromSearch } from "../lib/activeRail";
import { ApiError, api, type ActivePayload, type User } from "../lib/api";
import { activeTabVisible } from "../lib/grace";
import { disablePush } from "../lib/push";
import { useGroupBy } from "../lib/useGroupBy";
import { useMultiList } from "../lib/useMultiList";
import { useViewScope } from "../lib/useViewScope";
import { shouldRefreshOnWake, useWakeUp } from "../lib/wakeup";
import { Active } from "../screens/Active";
import { ForgotPassword } from "../screens/ForgotPassword";
import { ResetPassword } from "../screens/ResetPassword";
import { Unsubscribe } from "../screens/Unsubscribe";
import { VerifyEmail } from "../screens/VerifyEmail";
import { VerifyNewEmail } from "../screens/VerifyNewEmail";
import { ManageFencers } from "../screens/ManageFencers";
import { ManageNotifications } from "../screens/ManageNotifications";
import { Notifications } from "../screens/Notifications";
import { Onboarding } from "../screens/Onboarding";
import { Past } from "../screens/Past";
import { Pending } from "../screens/Pending";
import { Settings } from "../screens/Settings";
import { SignIn } from "../screens/SignIn";
import { Upcoming } from "../screens/Upcoming";
import { AppShell } from "./AppShell";
import { lazyScreen } from "./lazyScreen";

// The admin screens load on demand (decision 990): nobody else can open them.
const Admin = lazyScreen(() => import("../screens/Admin").then((m) => m.Admin));
const AccountDetail = lazyScreen(() => import("../screens/AccountDetail").then((m) => m.AccountDetail));
const AccountManagement = lazyScreen(() => import("../screens/AccountManagement").then((m) => m.AccountManagement));
const DuplicateFencers = lazyScreen(() => import("../screens/DuplicateFencers").then((m) => m.DuplicateFencers));
const Health = lazyScreen(() => import("../screens/Health").then((m) => m.Health));
const Metrics = lazyScreen(() => import("../screens/Metrics").then((m) => m.Metrics));
const PointsLists = lazyScreen(() => import("../screens/PointsLists").then((m) => m.PointsLists));
const PushDevices = lazyScreen(() => import("../screens/PushDevices").then((m) => m.PushDevices));
const UnresolvedNames = lazyScreen(() => import("../screens/UnresolvedNames").then((m) => m.UnresolvedNames));

export interface Session {
  user: User | null;
  /**
   * Live events across ALL the caller's lists, seeded by `/api/me` and
   * refreshed only by the wake-up check (decisions 503, 384).
   *
   * WHAT THIS NO LONGER DECIDES: the Active tab (decision 546). Its only time
   * bound is the tournament's date window, so it stays non-zero for a day after
   * the last fencer has finished — 72, on the Monday after a weekend whose
   * every tracked fencer was done. It still decides Active's "live on another
   * list" sentence, which is a claim about events rather than about fencers.
   *
   * THE LANDING DECISION MUST NOT WRITE THIS. `/api/active` is view-scoped and
   * defaults to the primary list, so a count taken from it would hide the tab
   * whenever the live event sits on a list the reader is not currently viewing
   * — which is the only route to the picker that would widen the scope.
   */
  activeEventCount: number;
  /**
   * The Active tab's own two facts (decision 546), seeded and refreshed exactly
   * as the count above is. `activeFencerCount` is the tracked population still
   * on screen for a reason that does not age out; `lastFencerDoneAt` is when
   * the rest of them finished, and `activeTabVisible` ages it with the window
   * that retires a fencer card.
   */
  activeFencerCount: number;
  lastFencerDoneAt?: string;
  /**
   * Unread alerts, seeded by `/api/me`, refreshed by the wake-up check and
   * cleared by the feed when it advances the watermark. The shell always renders
   * the notifications entry and shows this only when non-zero (decision 401).
   */
  unreadAlertCount: number;
  /**
   * The build the server was compiled from (decision 361), for the Settings
   * footer. Optional because the /api/me failure path has nothing to say.
   */
  serverVersion?: string;
  serverCommit?: string;
  /**
   * The VAPID application server key (stage 4a), for the device enrolment
   * control on the notifications page.
   *
   * Optional for the same reason the build identity is: the /api/me failure path
   * has nothing to say. Absent or empty means this build cannot enrol a device,
   * which the control treats as the state a browser with no push support gives
   * it — one line, no control.
   */
  pushPublicKey?: string;
  /**
   * The account's feature grants (decision 369), seeded by `/api/me`. Held on
   * the session rather than fetched per screen: the server already sends them
   * with everything else the shell needs, and two reads of one fact are two
   * answers waiting to disagree.
   *
   * Defaulted to empty on the failure path, which is the safe direction: gated
   * chrome stays hidden and the server enforces regardless.
   */
  features: string[];
  /**
   * The member cap from `/api/me` (decision 889), for onboarding's "N of M",
   * which renders before the account has a list to carry it. Absent when the
   * server could not read it; carried through a preference edit like the
   * deployment facts above.
   */
  listMemberCap?: number;
  /**
   * The list cap from `/api/me` (decision 889), so Manage fencers offers no
   * "New list" an account may not use. Absent when the server could not read
   * it, and then the button stays and the server enforces.
   */
  listCountCap?: number;
}

export interface AppRoutesProps {
  session: Session;
  onUserChanged: (user: User | null) => void;
  onUnreadCountChanged: (count: number) => void;
}

/**
 * THE LINK PAGES SIT IN FRONT OF THE GATE (stage 4b phase D). A verification,
 * reset or change-of-address link is opened from an inbox, in whatever state
 * this browser happens to be in -- signed out, pending, mid-onboarding, or
 * signed in -- and each page works in all of them, because the token in its URL
 * is the credential rather than the session. Behind the gate they would be
 * unreachable from exactly the states that need them most: a reset link is
 * opened by someone who cannot sign in.
 *
 * Every other path falls through to the gate below, unchanged.
 */
export function AppRoutes(props: AppRoutesProps) {
  const { session, onUserChanged } = props;
  const signedIn = session.user !== null;
  return (
    <Routes>
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword onSignedIn={onUserChanged} />} />
      <Route
        path="/verify-email"
        element={<VerifyEmail signedIn={signedIn} onUserChanged={onUserChanged} />}
      />
      <Route
        path="/verify-new-email"
        element={<VerifyNewEmail signedIn={signedIn} onUserChanged={onUserChanged} />}
      />
      {/* A summary's unsubscribe link (decision 621), for the same reason: the
          token is the credential, and it is opened from an inbox. */}
      <Route path="/unsubscribe" element={<Unsubscribe signedIn={signedIn} />} />
      <Route path="*" element={<GatedRoutes {...props} />} />
    </Routes>
  );
}

function GatedRoutes({
  session,
  onUserChanged,
  onUnreadCountChanged,
}: AppRoutesProps) {
  const {
    user,
    activeEventCount,
    activeFencerCount,
    lastFencerDoneAt,
    unreadAlertCount,
    serverVersion,
    serverCommit,
    pushPublicKey,
    features,
    listMemberCap,
    listCountCap,
  } = session;

  const navigate = useNavigate();

  const signOut = useCallback(() => {
    // The server clears the cookie and revokes the row; the client forgets the
    // user. Deliberately NOT a client-side-only forget: a session that still
    // works but is invisible to the app is the worst of both.
    //
    // AND THE URL GOES TOO (decision 355, roadmap item 6). The signed-out
    // router serves the sign-in form from path="*" without changing the URL,
    // so "/settings" would survive the gate — the exit-side twin of the entry
    // decision 330 fixed — and the NEXT sign-in would land wherever the last
    // session happened to end instead of at the landing rule. `replace`, so
    // the abandoned path is not one Back-press away from a sign-in form.
    // THIS DEVICE IS UNENROLLED FIRST, AND IT IS A LEAK IF IT IS NOT. A
    // browser's push subscription belongs to the browser PROFILE, not to whoever
    // is signed in: it survives a sign-out and survives a different person
    // signing in on the same phone. Leaving the row behind would send this
    // account's fencer's check-ins and strip assignments to the next person to
    // use the device.
    //
    // BEFORE the sign-out, because the delete needs the session cookie that
    // sign-out is about to clear. Best-effort inside disablePush, so a push
    // endpoint being unreachable cannot hold a sign-out hostage.
    void disablePush(api.unsubscribePush)
      .catch(() => undefined)
      .then(() => api.signOut())
      .finally(() => {
        navigate("/", { replace: true });
        onUserChanged(null);
      });
  }, [onUserChanged, navigate]);

  // --- the gate ---

  if (!user) {
    return (
      <Routes>
        <Route path="/create-account" element={<SignIn mode="create-account" onSignedIn={onUserChanged} />} />
        {/*
          Everything else, including a deep link to /active, lands on sign-in.
          The path is NOT preserved for a post-sign-in redirect, and that is a
          deliberate omission rather than an oversight: the landing rule already
          sends a signed-in user to the screen that has something on it, which is
          a better answer than the URL they happened to have bookmarked.
        */}
        <Route path="*" element={<SignIn mode="sign-in" onSignedIn={onUserChanged} />} />
      </Routes>
    );
  }

  if (user.status === "pending") {
    // ONE screen for every path. A pending account has no app to navigate, so
    // giving this state a route set would create URLs that mean nothing.
    return <Pending user={user} onSignOut={signOut} onUserChanged={onUserChanged} />;
  }

  if (user.status === "disabled") {
    // Same treatment as pending, and the same screen: §12 has no separate design
    // for it, and the honest content is identical — you cannot get in, and a
    // person is the remedy. Recorded rather than invented.
    return <Pending user={user} onSignOut={signOut} onUserChanged={onUserChanged} />;
  }

  // Approved, but onboarding never finished: no list exists. Detected from the
  // absence of a current list rather than by probing an endpoint for a 409, so
  // the first render is already correct.
  if (!user.current_list_id) {
    // The VAPID key rides along for onboarding's third step (decision 498). It
    // is passed rather than fetched there for the same reason the notifications
    // page takes it as a prop: `pushManager.subscribe` runs inside the gesture,
    // and a round trip between the tap and the prompt loses it.
    // The user rides along for the email-summaries step, which names the
    // address and whether it is verified yet (decision 623).
    // The member cap rides along for the search's "N of M" (decision 889).
    return (
      <Onboarding
        user={user}
        onDone={onUserChanged}
        {...(pushPublicKey ? { pushPublicKey } : {})}
        {...(listMemberCap !== undefined ? { cap: listMemberCap } : {})}
      />
    );
  }

  return (
    <Routes>
      <Route
        element={
          <AppShell
            user={user}
            activeFencerCount={activeFencerCount}
            {...(lastFencerDoneAt ? { lastFencerDoneAt } : {})}
            unreadAlertCount={unreadAlertCount}
          />
        }
        path="/"
      >
        <Route
          index
          element={
            <Landing
              activeFencerCount={activeFencerCount}
              {...(lastFencerDoneAt ? { lastFencerDoneAt } : {})}
            />
          }
        />
        {/*
          THE THREE VIEWS TAKE `features` NOW (multi-list build item 3), because
          the list-scope control is gated on the grant and the session is where
          the grant already is (decision 369). Active additionally takes the live
          count, which its scope-empty state states when a narrower scope is
          hiding events on other lists (decision 503).
        */}
        <Route
          path="active"
          element={
            <ActiveRoute
              features={features}
              activeEventCount={activeEventCount}
              activeFencerCount={activeFencerCount}
              {...(lastFencerDoneAt ? { lastFencerDoneAt } : {})}
            />
          }
        />
        <Route
          path="upcoming"
          element={
            <UpcomingRoute user={user} features={features} onUserChanged={onUserChanged} />
          }
        />
        <Route
          path="past"
          element={
            <PastRoute user={user} features={features} onUserChanged={onUserChanged} />
          }
        />
        <Route
          path="settings"
          element={
            <Settings
              user={user}
              onUserChanged={onUserChanged}
              onSignOut={signOut}
              // The account and its session are gone (decision 891): forget the
              // user and go home, as sign-out does once the server has answered.
              onAccountDeleted={() => {
                navigate("/", { replace: true });
                onUserChanged(null);
              }}
              {...(serverVersion ? { serverVersion } : {})}
              {...(serverCommit ? { serverCommit } : {})}
            />
          }
        />
        <Route
          path="settings/fencers"
          element={
            <ManageFencers
              user={user}
              features={features}
              onUserChanged={onUserChanged}
              {...(listCountCap !== undefined ? { listCountCap } : {})}
            />
          }
        />
        <Route
          path="settings/notifications"
          element={
            /*
              THE USER OBJECT IS THREADED IN the way `features` and
              `pushPublicKey` already are (decision 535). The page's new
              name-format control writes through the WHOLE-OBJECT preferences
              PUT, so it needs every field the user carries, and the session
              lives here — a second `/api/me` from the screen would be a second
              copy of state this component already holds.
            */
            <ManageNotifications
              user={user}
              features={features}
              pushPublicKey={pushPublicKey}
              onUserChanged={onUserChanged}
            />
          }
        />
        {/*
          The feed. Not lazy-loaded: it is one screen of one endpoint, and a code
          split here would trade a first-paint delay for a bundle saving nobody
          has measured a need for.
        */}
        <Route
          path="notifications"
          element={
            /*
              The feed names the tracked fencer in the reader's own chosen
              format (decision 535), so it takes the value rather than the whole
              user: the screen has no other use for one, and passing the setting
              alone says what it depends on.
            */
            <Notifications
              nameFormat={user.notification_name_format}
              onUnreadCountChanged={onUnreadCountChanged}
            />
          }
        />
        {/*
          NOT ROLE-GATED IN THE ROUTER, and that is deliberate rather than an
          oversight: the gate is the API, which answers 403 to every admin endpoint
          for a non-admin (requireAdmin in session.go). A client-side route guard
          would be security theatre — anyone can edit the bundle — and its real cost
          is that it invites the belief the server is already guarded. What the
          client DOES do is hide the tab (AppShell), because offering a link to a
          screen that will refuse you is a different failure.
        */}
        <Route path="admin" element={<Admin />} />
        {/*
          The two admin jobs that grew their own pages (decision 474). Sibling
          routes rather than a nested outlet: they share no chrome with the hub
          beyond the shell, and each is a whole screen. Both are bookmarkable,
          which is what somebody who works the queue daily will want, and both
          are reached by a real anchor so cmd-click keeps the hub open.

          Not role-gated here either, for the reason above: the API is the gate.
        */}
        {/*
          THE SELECTION IS IN THE PATH (decision 481). Both screens are
          master-detail, and the selected row used to be component state — so
          `back` from a detail on a phone went to `/admin`, because there was
          nothing between the list and the hub for it to land on. Principle 1
          makes the fix the same thing that makes a detail linkable: put the
          selection in the URL.

          A PATH SEGMENT RATHER THAN A SEARCH PARAM, because it names a
          RESOURCE. Search params in this app carry view state — `?layout=a|b`,
          the grouping, the query — and a param would have made the detail
          addressable without making it a place. The consequence a reviewer
          feels: `/admin/unresolved/<id>` can be sent to somebody and it opens
          on that alias.

          The list's OWN state stays in search params and rides along on the
          links, so a deep link names one resource without resetting the page
          behind it.

          THE QUEUE'S two routes render the SAME component, because its detail
          pane is one reflowing layout with its list (§12) and there is nothing
          for an outlet to fill. ACCOUNTS' two render different ones, since
          multi-list build item 5: an account is a place you go rather than a
          pane beside the directory, so the path that names one renders a screen
          of its own. Same URLs, and the difference is deliberate — the two
          screens do different work (see AccountDetail.tsx's header).
        */}
        <Route path="admin/unresolved" element={<UnresolvedNames />} />
        <Route path="admin/unresolved/:aliasID" element={<UnresolvedNames />} />
        <Route path="admin/accounts" element={<AccountManagement />} />
        <Route path="admin/accounts/:accountID" element={<AccountDetail />} />
        {/* The third entry under the hub's "More tools" (decision 483). */}
        <Route path="admin/metrics" element={<Metrics />} />
        <Route path="admin/metrics/points-lists" element={<PointsLists />} />
        <Route path="admin/metrics/push" element={<PushDevices />} />
        {/* The fourth (decision 525): a second, read-mostly review queue, no
            detail route because there is no per-row page -- the row IS the
            detail. */}
        <Route path="admin/duplicate-fencers" element={<DuplicateFencers />} />
        {/* The fifth (roadmap stage 5): what the hourly ingestion health
            check found. Metrics links here rather than repeating it. */}
        <Route path="admin/health" element={<Health />} />
        {/*
          An already-signed-in user hitting the sign-in URL. Redirected rather
          than shown a form: they are signed in, and a form that would sign them
          in again is a dead end that looks like a bug.
        */}
        <Route path="sign-in" element={<Navigate to="/" replace />} />
        <Route path="create-account" element={<Navigate to="/" replace />} />
        {/*
          An unknown client-side path. The server serves the shell for it — that
          is what makes deep links work — so the 404 has to happen here. A
          redirect to the landing decision rather than a 404 screen: every path
          under this app is one of a handful of screens, so an unknown one is a
          stale link, and landing somewhere useful beats an apology. `replace` so
          it does not become a history entry to click back through.
        */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/**
 * "/" is a decision, not a screen.
 *
 * IT FOLLOWS THE ACTIVE TAB (Remenyik): the same rule, `activeTabVisible`, over
 * the same two facts `/api/me` already gave the shell. It used to count events
 * from `/api/active`, whose only time bound is the tournament's date window, so
 * the day after an event — tab hidden, every card retired — the logo still
 * landed on an Active screen the reader had no tab to return to. Two rules for
 * one question drifted exactly where 546 said they would, either side of a
 * midnight. The logo, sign-in and sign-out all land on "/" (330, 355), so all
 * three now agree with the tab.
 *
 * No request, so there is no in-flight state to render. It publishes nothing
 * either (503): the facts are the shell's and are only read here.
 *
 * A FAILED /api/me LANDS ON UPCOMING, as a failed request always has (324): the
 * session's failure path carries no live fencers, and Upcoming is the stated
 * default and always has content.
 */
function Landing({
  activeFencerCount,
  lastFencerDoneAt,
}: {
  activeFencerCount: number;
  lastFencerDoneAt?: string;
}) {
  const live = activeTabVisible({ activeFencerCount, lastFencerDoneAt });
  return <Navigate to={live ? "/active" : "/upcoming"} replace />;
}

/**
 * Active's data container.
 *
 * Split from the Active component, which is pure over its payload. That is what
 * lets every state in §9's inventory be tested and screenshotted without a server,
 * and it keeps the fetch policy in one place where the refresh question can be
 * answered deliberately.
 */
function ActiveRoute({
  features,
  activeEventCount,
  activeFencerCount,
  lastFencerDoneAt,
}: {
  features: string[];
  activeEventCount: number;
  activeFencerCount: number;
  lastFencerDoneAt?: string;
}) {
  const { t } = useTranslation();
  const { search } = useLocation();
  const [payload, setPayload] = useState<ActivePayload | null>(null);
  const [failed, setFailed] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  // Bumped to re-run the fetch. A counter rather than calling the fetch
  // directly, so there is still exactly ONE place that fetches and the
  // cancelled-on-unmount guard covers the wake path for free.
  const [reloads, setReloads] = useState(0);

  /*
   * THE TWO CONDITIONS, WRITTEN OUT HERE AND IN EACH OF THE OTHER TWO ROUTES
   * (decision 510). The FLAG decides whether any multi-list chrome exists; the
   * COUNT decides whether a picker does, and this control needs both. The pair
   * is not hoisted into something shared: a helper answering "should chrome
   * render" is exactly what would let the next control needing only one of them
   * get the other for free, which is the collapse 510 exists to prevent.
   *
   * `useMultiList` reads the lists whether or not the flag is held, because the
   * count is the other condition and cannot be known without reading.
   * `useViewScope` is the opposite and is gated on both: an account that cannot
   * see the control makes no view-scope request at all.
   */
  const { granted, lists } = useMultiList(features);
  const multiList = granted && lists.length > 1;

  const scope = useViewScope(
    "active",
    multiList,
    // A SCOPE CHANGE REFETCHES, through the same counter the wake path uses, so
    // the rows and the control's label can never describe different scopes.
    useCallback(() => setReloads((n) => n + 1), []),
  );

  useEffect(() => {
    let cancelled = false;

    api
      .active()
      .then((p) => {
        if (cancelled) return;
        setPayload(p);
        setFailed(false);
        setFetchedAt(Date.now());
      })
      .catch((err: unknown) => {
        if (!cancelled && err instanceof ApiError) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [reloads]);

  /*
   * WAKING UP ON THE DAY-OF SCREEN. Five minutes, because that is already the
   * age at which this view stops trusting a strip it is displaying
   * (DAY_OF_AGING_MINUTES, §9) — a screen willing to call its own datum stale
   * should be willing to go and check.
   *
   * A failed previous load deliberately does NOT refetch here: `fetchedAt`
   * stays null, so a wake on a dead connection does not retry in a loop.
   */
  useWakeUp(
    useCallback(() => {
      if (shouldRefreshOnWake(fetchedAt, "day-of")) setReloads((n) => n + 1);
    }, [fetchedAt]),
  );

  if (failed) {
    // An honest sentence, and specifically NOT a retry button: the remedy for a
    // venue's wifi is to try again in a moment, which reloading already does.
    return (
      <div style={{ padding: "var(--tc-space-4)" }}>
        <p>{t("error.couldNotLoad")}</p>
      </div>
    );
  }

  if (!payload) return null;

  /*
   * ROADMAP ITEM 4'S REMAINING TOGGLE, read HERE rather than inside Active for
   * the reason the container exists at all: Active is pure over its props,
   * which is what lets every state in §9's inventory be tested and
   * screenshotted without a router. Reading `useLocation` inside it would put a
   * router dependency in the one component that deliberately has none.
   *
   * Prototype scaffolding — see `lib/activeRail.ts`. Absent or unrecognised
   * gives `none`, which is the standard screen.
   */

  /*
   * THE CONTROL IS A SLOT, not something Active builds. Active is pure over its
   * props — which is what lets every state in §9's inventory be tested and
   * screenshotted without a router — and this control reads the route to close
   * its panel (473). Passing the node in keeps the router dependency out here,
   * on the precedent decision 326 set for Manage fencers' commit bar.
   *
   * Null unless both conditions hold AND the scope has arrived: the label states
   * the scope, so a control rendered before the read lands would have nothing
   * true to say.
   */
  const listScope =
    multiList && scope.scope !== null ? (
      <ListScope
        lists={lists}
        scope={scope.scope}
        listIDs={scope.listIDs}
        saving={scope.saving}
        failed={scope.failed}
        onChange={scope.setScope}
        activeCounts={
          new Map((payload.active_by_list ?? []).map((r) => [r.list_id, r.active_people]))
        }
      />
    ) : null;

  /*
   * WHAT A NARROWER SCOPE IS HIDING (decision 503). Two of the three conditions
   * are checked HERE, where the data is: the scope is narrower than all lists,
   * and `/api/me`'s `active_event_count` — which counts over ALL the caller's
   * lists — is greater than zero. The third is `emptyReason`, which is Active's
   * own reading of its payload and stays there.
   *
   * The number is passed as it arrived, with no arithmetic: under those
   * conditions every counted live event is on a list this view is not showing,
   * so subtracting anything would be an inference.
   *
   * THIS DECIDES THE SENTENCE ONLY. Whether an empty Active keeps the control
   * is no longer asked here: it renders whenever `listScope` exists at all,
   * which is already the two conditions plus an arrived scope (540, 510).
   */
  const narrowed = scope.scope !== null && scope.listIDs.length < lists.length;
  /*
   * AND THE SENTENCE DECAYS WITH THE TAB (decision 546). `activeEventCount`
   * alone would still claim live events elsewhere all through the day after a
   * weekend, because its only time bound is the tournament's date window —
   * visible here, where the route outlives the tab that leads to it. The same
   * two facts the shell gates on gate the claim, so the app cannot say on one
   * screen what it has already stopped offering on the other.
   */
  const anythingLive = activeTabVisible({ activeFencerCount, lastFencerDoneAt });
  const hiddenLive = narrowed && anythingLive && activeEventCount > 0 ? activeEventCount : undefined;

  return (
    <Active
      payload={payload}
      rail={railFromSearch(search)}
      listScope={listScope}
      {...(hiddenLive === undefined ? {} : { hiddenLiveEventCount: hiddenLive })}
    />
  );
}

/*
 * `useOnce` USED TO LIVE HERE and is gone rather than kept unused. Its docstring
 * named Active, Upcoming and Past as its three callers; Past moved to a debounced
 * search, Upcoming has now followed, and Active polls. A helper whose comment
 * lists callers it no longer has reads as a feature and is dead code — the same
 * failure decision 324 recorded for an option nothing selected.
 */

function LoadFailed() {
  const { t } = useTranslation();
  return (
    <div style={{ padding: "var(--tc-space-4)" }}>
      <p>{t("error.couldNotLoad")}</p>
    </div>
  );
}

/**
 * A debounced server-side search over one payload — the mechanism Upcoming and
 * Past now share.
 *
 * EXTRACTED RATHER THAN COPIED. Past had this inline, and giving Upcoming "the
 * same search" by duplicating forty lines would have meant two debounces to keep
 * in step and two places for the stays-mounted behaviour below to drift. The
 * behaviour is identical on both screens because it is now literally the same
 * code; what differs is only which endpoint is called.
 *
 * THE PREVIOUS RESULTS STAY MOUNTED while a new query is in flight. Blanking the
 * list between keystrokes would make filtering feel like reloading; keeping it
 * and marking `aria-busy` is both calmer and more honest.
 */
function useSearched<T>(fetch: (search: string) => Promise<T>) {
  const [search, setSearch] = useState("");
  const [payload, setPayload] = useState<T | null>(null);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setSearching(true);

    // 250ms, the same as the fencer search, for the same reason: long enough that
    // typing a name is one request rather than eleven, short enough to still feel
    // like narrowing.
    const timer = setTimeout(() => {
      fetch(search.trim())
        .then((p) => {
          if (cancelled) return;
          setPayload(p);
          setFailed(false);
          setFetchedAt(Date.now());
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // `fetch` is a fresh closure each render; depending on it would re-fetch
    // every time and defeat the debounce. The search term is the real input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, reloads]);

  /*
   * WAKING UP ON A PLANNING SCREEN. An hour, not five minutes: in this regime
   * hours are normal and the design forbids saying otherwise (§10 — "a
   * registration list read this morning is not stale"), so refetching on a
   * five-minute-old payload would spend a request to redraw an identical
   * screen. An hour still means the tab left open overnight is not showing
   * yesterday's deadlines when it is picked up.
   *
   * SHARED BY UPCOMING AND PAST because the search mechanism already is —
   * putting the rule here rather than in each route is what stops the two
   * screens from drifting to different answers about the same question.
   */
  useWakeUp(
    useCallback(() => {
      if (shouldRefreshOnWake(fetchedAt, "planning")) setReloads((n) => n + 1);
    }, [fetchedAt]),
  );

  /*
   * REFETCH ON DEMAND, for the list-scope control (build item 3): a scope change
   * has to bring back different rows, and this hook owned the only fetch.
   *
   * The RETURN VALUE is what grew; the timing rules are untouched. A reload goes
   * through the same counter the wake path uses, so it pays the same 250ms
   * debounce the wake path already pays — one delayed fetch rather than a second
   * code path that could disagree with this one about anything.
   */
  const reload = useCallback(() => setReloads((n) => n + 1), []);

  return { search, setSearch, payload, searching, failed, reload };
}

/**
 * Upcoming's route.
 *
 * THE FENCER-AXIS COLLAPSE COMES OFF THE PAYLOAD (decision 501), where it used
 * to come from the shell's copy of `/api/me`. Each view carries its own list
 * scope now, so the two planning views can honestly disagree about how many
 * fencers there are — and a number held by the shell could only be right for
 * one of them. Reading it from the same response the rows came in also means
 * the toggle and the rows can never be answering for different scopes.
 */
function UpcomingRoute({
  user,
  features,
  onUserChanged,
}: {
  user: User;
  features: string[];
  onUserChanged: (user: User) => void;
}) {
  const [groupBy, setGroupBy] = useGroupBy("upcoming", user, onUserChanged);
  const { search, setSearch, payload, searching, failed, reload } = useSearched((s) =>
    api.upcoming(s),
  );

  // The two conditions, again written out at the point of use — see ActiveRoute.
  const { granted, lists } = useMultiList(features);
  const multiList = granted && lists.length > 1;
  const scope = useViewScope("upcoming", multiList, reload);
  /*
   * NARROWER THAN EVERY LIST, which is what keeps the control on an EMPTY page
   * (decision 521). Computed here because the route is what holds the list
   * count; the screen combines it with its own reading of "empty".
   */
  const narrowed = scope.scope !== null && scope.listIDs.length < lists.length;

  if (failed && !payload) return <LoadFailed />;
  if (!payload) return null;

  return (
    <Upcoming
      payload={payload}
      groupBy={groupBy}
      onGroupByChange={setGroupBy}
      singleFencer={payload.tracked_fencer_count <= 1}
      search={search}
      onSearchChange={setSearch}
      searching={searching}
      scopeNarrowed={narrowed}
      listScope={
        multiList && scope.scope !== null ? (
          <ListScope
            lists={lists}
            scope={scope.scope}
            listIDs={scope.listIDs}
            saving={scope.saving}
            failed={scope.failed}
            onChange={scope.setScope}
          />
        ) : null
      }
    />
  );
}

/**
 * Past's route. The search is server-side because §11 says so for a stated
 * reason: "this view grows forever; client-side filtering over a full download
 * doesn't scale". The mechanism is `useSearched`, shared with Upcoming — as is
 * the payload's own tracked-fencer count; see UpcomingRoute.
 */
function PastRoute({
  user,
  features,
  onUserChanged,
}: {
  user: User;
  features: string[];
  onUserChanged: (user: User) => void;
}) {
  const [groupBy, setGroupBy] = useGroupBy("past", user, onUserChanged);
  const { search, setSearch, payload, searching, failed, reload } = useSearched((s) =>
    api.past(s),
  );

  // The two conditions, again written out at the point of use — see ActiveRoute.
  const { granted, lists } = useMultiList(features);
  const multiList = granted && lists.length > 1;
  const scope = useViewScope("past", multiList, reload);
  /*
   * NARROWER THAN EVERY LIST, which is what keeps the control on an EMPTY page
   * (decision 521). Computed here because the route is what holds the list
   * count; the screen combines it with its own reading of "empty".
   */
  const narrowed = scope.scope !== null && scope.listIDs.length < lists.length;

  if (failed && !payload) return <LoadFailed />;
  if (!payload) return null;

  return (
    <Past
      payload={payload}
      groupBy={groupBy}
      onGroupByChange={setGroupBy}
      singleFencer={payload.tracked_fencer_count <= 1}
      search={search}
      onSearchChange={setSearch}
      searching={searching}
      scopeNarrowed={narrowed}
      listScope={
        multiList && scope.scope !== null ? (
          <ListScope
            lists={lists}
            scope={scope.scope}
            listIDs={scope.listIDs}
            saving={scope.saving}
            failed={scope.failed}
            onChange={scope.setScope}
          />
        ) : null
      }
    />
  );
}
